import { useTranslation } from '@crew/modules/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import {
  Column,
  Toolbar,
  Item,
  TreeList,
  RowDragging,
} from 'devextreme-react/tree-list'
import {
  memo,
  useCallback,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
} from 'react'
import _ from 'lodash'

import EventAvailable from '~icons/material-symbols/event-available'
import AccountTreeOutline from '~icons/material-symbols/account-tree-outline'
import BaselineDelete from '~icons/ic/baseline-delete'
import BaselineError from '~icons/ic/baseline-error'
import BaselineWarning from '~icons/ic/baseline-warning'
import FormatIndentDecrease from '~icons/material-symbols/format-indent-decrease'
import FormatIndentIncrease from '~icons/material-symbols/format-indent-increase'
import {
  FormValues,
  useTaskTemplateTaskListAndEntryForm,
} from './useTaskTemplateTaskListAndEntryForm'
import { CrewHtmlEditorField } from 'components/forms/crewHtmlEditorField'
import { CrewSelectBoxField } from 'components/forms/crewSelectBoxField'
import { CrewTextBoxField } from 'components/forms/crewTextBoxField'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { CrewFieldLabel } from 'components/elements/crewFieldLabel'
import { CrewNumberBoxField } from 'components/forms/crewNumberBoxField'
import { useAppSelector } from 'states/hooks'
import { CrewTaskPrioritySelectBoxFieldRender } from 'components/elements/crewTaskPrioritySelectBoxFieldRender/crewTaskPrioritySelectBoxFieldRender'
import { CrewTaskPrioritySelectBoxItemRender } from 'components/elements/crewTaskPrioritySelectBoxItemRender/crewTaskPrioritySelectBoxItemRender'
import { TaskKindRef, UserRef } from '@crew/models/refs'
import { CrewBadgeSelectBoxField } from 'components/forms/crewBadgeSelectBoxField'
import { useModal } from 'components/layouts/modal/useModal'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { CrewPopover } from 'components/devextreme/crewPopover'
import { FocusedRowChangedEvent, Node } from 'devextreme/ui/tree_list'
import { getEnumDataByValue } from '@crew/utils/enum'
import { TaskPriorities } from 'enums/app'
import { CrewTaskPriority } from 'components/elements/crewTaskPriority/crewTaskPriority'
import { CrewBadge } from 'components/elements/crewBadge/crewBadge'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { uniqueString } from '@crew/utils'
import { useGetMaxTasksDepthLimitQuery } from '@crew/apis/task/taskApis'
import { useToast } from 'hooks/useToast'
import { reassignTaskOrder } from 'utils/taskTemplate'

export type TreeListTaskTemplateTask = {
  taskOrder: string
  parentTaskTemplateTaskOrder: string | null
  subject: string
  taskKindId: string
  taskKind: TaskKindRef | null
  taskPriority: number
  assignToUserId: string | null
  assignToUser: UserRef | null
  requiredDays: number | null
  description: string
}

export type TaskTemplateTaskListAndEntryFormProps = {
  projectId: string
  taskTemplateTasks: TreeListTaskTemplateTask[]
  setTaskTemplateTasks: (taskTemplateTasks: TreeListTaskTemplateTask[]) => void
}

export type TaskTemplateTaskListAndEntryFormHandle = {
  getCurrentFormValues: () => (FormValues & { key: string }) | null
}

/**
 * 新しいtaskOrderを生成する関数
 * @param parentTaskTemplateTaskOrder 親taskOrder (null の場合はルートレベル)
 * @param taskTemplateTasks すべてのタスクリスト
 * @returns 新しいtaskOrder (例: "1", "2-1", "3-2-1")
 */
const generateNextTaskOrder = (
  parentTaskTemplateTaskOrder: string | null,
  taskTemplateTasks: TreeListTaskTemplateTask[]
): string => {
  // 同じ親を持つ兄弟タスクを取得
  const siblings = taskTemplateTasks.filter(
    (task) => task.parentTaskTemplateTaskOrder === parentTaskTemplateTaskOrder
  )

  if (siblings.length === 0) {
    // 兄弟がいない場合
    if (!parentTaskTemplateTaskOrder) {
      // ルートレベル: "1" から開始
      return '1'
    } else {
      // 子タスク: 親taskOrder + "-1"
      return `${parentTaskTemplateTaskOrder}-1`
    }
  }

  // 兄弟の中で最大の番号を見つける
  const maxNumber = siblings.reduce((max, sibling) => {
    const orderParts = sibling.taskOrder.split('-')
    const lastNumber = parseInt(orderParts[orderParts.length - 1], 10)
    return Math.max(max, lastNumber)
  }, 0)

  // 次の番号を生成
  if (!parentTaskTemplateTaskOrder) {
    // ルートレベル
    return `${maxNumber + 1}`
  } else {
    // 子タスク
    return `${parentTaskTemplateTaskOrder}-${maxNumber + 1}`
  }
}

/**
 * 親タスクの所要日数を子タスクの合計に揃える
 * - 子に requiredDays がなければ合計に含めない（全て未入力なら親は null のまま）
 */
const recalcParentRequiredDays = (
  tasks: TreeListTaskTemplateTask[]
): TreeListTaskTemplateTask[] => {
  // taskOrderをキーとし、そのtaskOrderを親に持つ子タスクの!!index!!の配列を持つmap
  // 配列を参照するときにtaskOrderベースだとfilterが必要なため
  const childrenTaskTemplateTaskIndexMap = new Map<string | null, number[]>()

  tasks.forEach((task, index) => {
    const parent = task.parentTaskTemplateTaskOrder
    if (!childrenTaskTemplateTaskIndexMap.has(parent)) {
      // index がまだ登録されていない場合は、キーとして登録する
      childrenTaskTemplateTaskIndexMap.set(parent, [])
    }

    // has()で対象のindexがあるのは確認できているので、pushする
    childrenTaskTemplateTaskIndexMap.get(parent)?.push(index)
  })

  let updatedTasks = tasks

  // taskOrderのindexをキーとし、計算済みのrequiredDaysをキャッシュするマップ
  const requiredDaysCache = new Map<number, number | null>()

  // 再帰で指定 index の requiredDays を計算し、結果をメモ化する関数
  const getAggregatedRequiredDays = (index: number): number | null => {
    const cachedValue = requiredDaysCache.get(index)

    if (cachedValue) {
      // キャッシュが存在する場合はそれを返す
      return cachedValue
    }

    // 引数の index のタスクの子タスクを取得
    const childrenTaskTemplateTaskIndexes =
      childrenTaskTemplateTaskIndexMap.get(tasks[index].taskOrder)
    if (
      !childrenTaskTemplateTaskIndexes ||
      childrenTaskTemplateTaskIndexes.length === 0
    ) {
      // 子タスクが存在しない場合は、自分の値を返す
      const own = tasks[index].requiredDays ?? null
      requiredDaysCache.set(index, own)
      return own
    }

    // 子タスクがある場合は再帰で集計する
    const childValues = childrenTaskTemplateTaskIndexes
      .map((childIndex) => getAggregatedRequiredDays(childIndex))
      // typeguard, nullを除外しておく
      .filter((value): value is number => value !== null && value !== undefined)

    const aggregated =
      childValues.length === 0
        ? null
        : // 合計を計算
          childValues.reduce((sum, value) => sum + value, 0)

    // キャッシュに保存しておき、再計算を防ぐ
    requiredDaysCache.set(index, aggregated)
    return aggregated
  }

  // すべてのタスクを走査し、変更があれば更新する
  tasks.forEach((task, index) => {
    const nextRequiredDays = getAggregatedRequiredDays(index)
    const prevRequiredDays = task.requiredDays ?? null

    if (prevRequiredDays !== nextRequiredDays) {
      if (updatedTasks === tasks) {
        updatedTasks = [...tasks]
      }
      updatedTasks[index] = { ...task, requiredDays: nextRequiredDays }
    }
  })

  return updatedTasks
}

/**
 * 階層制限内でノードを移動できるか判定する
 * @param sourceNode 移動するノード
 * @param parentNode 移動先の親ノード
 * @param maxTasksDepthLimit 最大階層制限 (null = 無制限)
 * @returns true: 移動可 / false: 移動不可
 */
const isWithinTaskDepthLimit = (
  sourceNode: Node,
  parentNode: Node,
  maxTasksDepthLimit: number | null
): boolean => {
  if (!maxTasksDepthLimit) {
    return true
  }

  const getMaxDescendantLevel = (node: Node): number => {
    // 子ノードが存在しない場合は自身の階層を返す
    if (!node.children || node.children.length === 0) return node.level
    // 子ノードの階層を取得
    const childLevels = node.children.map(getMaxDescendantLevel)
    return Math.max(...childLevels)
  }

  // 移動元ノード配下で最も深い階層
  const maxDescendantLevel = getMaxDescendantLevel(sourceNode)

  // 移動後の sourceNode の階層
  const newSourceNodeLevel = parentNode.level + 1

  // 階層差分
  const levelIncrease = newSourceNodeLevel - sourceNode.level

  // 移動後の最深階層
  const newMaxDescendantLevel = maxDescendantLevel + levelIncrease

  // 制限階層を超えた場合はNG
  if (newMaxDescendantLevel >= maxTasksDepthLimit) return false

  return true
}

// タスクテンプレートのTreeListとタスクエントリフォームのコンポーネント
// TreeListとフォームはコンポーネントとしては分離するべきだが、呼び出し側でフォームの値の取得の必要があることや、TreeListとフォーム側の値のやり取りの観点でフラットな方が良かったため1つにしている
export const TaskTemplateTaskListAndEntryForm = memo(
  forwardRef<
    TaskTemplateTaskListAndEntryFormHandle,
    TaskTemplateTaskListAndEntryFormProps
  >((props, ref) => {
    const { t } = useTranslation()
    const toast = useToast()
    const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

    // タスクの最大階層数。nullは無制限
    const { data: maxTasksDepthLimitResult } = useGetMaxTasksDepthLimitQuery()
    const maxTasksDepthLimit = useMemo(() => {
      return maxTasksDepthLimitResult?.maxTasksDepthLimit ?? null
    }, [maxTasksDepthLimitResult])

    const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
      useModal()
    const [confirmMessage, setConfirmMessage] = useState('')

    // 現在フォーカスが当たっている行のキー
    const [focusedRowKey, setFocusedRowKey] = useState<string>()

    // focusedRowKeyを更新する関数。stateとtreeList両方を更新する
    const updateFocusedRowKey = useCallback((key: string | undefined) => {
      const treeList = treeListRef.current?.instance
      const currentKey = treeList?.option('focusedRowKey')
      if (treeList && currentKey !== key) {
        treeList.option('focusedRowKey', key)
      }

      setFocusedRowKey(key)
    }, [])

    const [initialTaskKindId, setInitialTaskKindId] = useState<string | null>(
      null
    )
    const [initialTaskKind, setInitialTaskKind] = useState<TaskKindRef | null>(
      null
    )

    // treeListのref
    const treeListRef = useRef<TreeList>(null)

    // ドラッグ操作時にフォーム値の保存が二重に行われないよう制御するフラグ
    // ドラッグ後に handleFocusedRowChanged が発火し、旧focusedRowKeyで保存しようとする問題を防ぐ
    const isDraggingRef = useRef(false)

    const {
      control,
      setValue,
      getValues,
      reset,
      taskKindDataSource,
      taskPriorityDataSource,
      memberDataSource,
      validateRules,
    } = useTaskTemplateTaskListAndEntryForm(props.projectId)

    const requiredDaysPopoverId = useMemo(
      () => `required-days-warning-${uniqueString()}`,
      []
    )

    // フォーカス行に子タスクがあるか
    const focusedRowHasChildren = useMemo(() => {
      if (!focusedRowKey) return false
      return props.taskTemplateTasks.some(
        (task) => task.parentTaskTemplateTaskOrder === focusedRowKey
      )
    }, [focusedRowKey, props.taskTemplateTasks])

    // 子タスクを持つタスクのtaskOrder一覧
    const taskOrdersHavingChildren = useMemo(() => {
      const parents: string[] = []
      props.taskTemplateTasks.forEach((task) => {
        const parent = task.parentTaskTemplateTaskOrder
        if (parent && !parents.includes(parent)) {
          parents.push(parent)
        }
      })
      return parents
    }, [props.taskTemplateTasks])

    // タスク作成時の初期値
    const initTaskTemplateTaskValues: TreeListTaskTemplateTask = useMemo(
      () => ({
        taskOrder: '',
        parentTaskTemplateTaskOrder: null,
        subject: '',
        taskKindId: initialTaskKindId || '',
        taskKind: initialTaskKind,
        taskPriority: TaskPriorities.Normal.value,
        assignToUserId: null,
        assignToUser: null,
        requiredDays: null,
        description: '',
      }),
      [initialTaskKind, initialTaskKindId]
    )

    // 初期化
    // 初期タスク種別IDとTaskKindRefを取得、また、memberDataSourceをロードする
    // memberDataSourceのロードは担当者のドロップダウン押下時に走るため、その前にフォーカス行を変更した場合に、nameなどが入らなくなるため
    useEffect(() => {
      const fetchTaskKinds = async () => {
        // 初回ロード
        //
        await memberDataSource.load()

        const loadedTaskKinds =
          (await taskKindDataSource.load()) as TaskKindRef[]

        if (loadedTaskKinds.length === 0) return

        // taskKindDataSourceからtaskKindsを取得する
        // 初期タスク種別を取得
        const initialTaskKind = loadedTaskKinds.find(
          (taskKind) => taskKind.initialValue
        )

        // 初期タスク種別が見つかった場合
        if (initialTaskKind) {
          // 初期タスク種別を設定
          setInitialTaskKindId(initialTaskKind.id)
          setInitialTaskKind(initialTaskKind)
        } else {
          // 初期値の設定されている項目が無ければそのテナントの最初の項目
          setInitialTaskKindId(loadedTaskKinds[0].id)
          setInitialTaskKind(loadedTaskKinds[0])
        }
      }
      fetchTaskKinds()
    }, [memberDataSource, taskKindDataSource])

    // 親コンポーネントから呼び出せるメソッドを公開
    useImperativeHandle(ref, () => ({
      getCurrentFormValues: () => {
        // 現在編集中のフォームの値で更新されたtaskTemplateTasksを返す
        if (!focusedRowKey) {
          return null
        }

        return {
          key: focusedRowKey,
          subject: getValues('subject'),
          taskKindId: getValues('taskKindId'),
          taskPriority: getValues('taskPriority'),
          assignToUserId: getValues('assignToUserId'),
          requiredDays: getValues('requiredDays'),
          description: getValues('description'),
        }
      },
    }))

    // ---------- ここからToolbarのボタン系の処理

    // 全折りたたみボタン
    const handleCollapseAllButtonClick = useCallback(() => {
      if (treeListRef.current) {
        // 即座に全て閉じる
        treeListRef.current.instance.option('expandedRowKeys', [])
      }
    }, [])

    const collapseAllButtonOptions = {
      icon: 'dx-icon dx-gantt-i dx-gantt-i-collapse',
      stylingMode: 'text',
      hint: t('action.closeAll'),
      onClick: handleCollapseAllButtonClick,
    }

    // 全展開ボタン
    const handleExpandAllButtonClick = useCallback(() => {
      if (treeListRef.current) {
        // すべての行キーを指定して強制展開する
        const allKeys = props.taskTemplateTasks.map((task) => task.taskOrder)
        treeListRef.current.instance.option('expandedRowKeys', allKeys)
      }
    }, [props.taskTemplateTasks])

    const expandAllButtonOptions = {
      icon: 'dx-icon dx-gantt-i dx-gantt-i-expand',
      stylingMode: 'text',
      hint: t('action.expandAll'),
      onClick: handleExpandAllButtonClick,
    }

    // アウトデントボタン（左）
    const handleOutdentButtonClick = useCallback(async () => {
      const treeList = treeListRef.current?.instance
      if (!treeList) return

      const currentFocusedRowKey = treeList.option('focusedRowKey')
      const selectedNode = treeList.getNodeByKey(currentFocusedRowKey)
      // 選択されたノードがルートノードまたは見つからない場合は何もしない
      if (
        !selectedNode ||
        selectedNode.parent === undefined ||
        selectedNode.parent.level === -1 // 選択した親のlevelが-1の場合はルートノード
      ) {
        return
      }

      const grandParentNode = selectedNode.parent.parent
      const isRootNode = grandParentNode?.level === -1

      // 選択されたノードの親taskOrderを更新
      const mergedTaskTemplateTasks = props.taskTemplateTasks.map(
        (taskTemplateTask) => {
          if (taskTemplateTask.taskOrder === selectedNode.key) {
            return {
              ...taskTemplateTask,
              // 「アウトデント対象の親ノードの親」によって処理を振り分ける
              // 1. ルートノードの場合はnull
              //    これにより、表示上の最上位階層となる
              // 2. それ以外は親ノードの親のtaskOrderに更新
              //    これにより、親と同じ階層になる
              parentTaskTemplateTaskOrder: isRootNode
                ? null
                : (grandParentNode?.data?.taskOrder as string),
            }
          }
          return taskTemplateTask
        }
      )

      // taskOrderを再採番
      const { tasks: reassignedTaskTemplatesTasks } = reassignTaskOrder(
        mergedTaskTemplateTasks
      )
      const recalculatedTasks = recalcParentRequiredDays(
        reassignedTaskTemplatesTasks
      )
      props.setTaskTemplateTasks(recalculatedTasks)

      // アウトデントの場合はフォーカス行は変わらないので、インデント時と異なり再計算は不要
    }, [props])

    // アウトデントボタンのカスタムレンダー
    const renderOutdentButton = useCallback(
      ({ disabled }: { disabled: boolean }) => {
        return (
          <CrewButton
            icon={<FormatIndentDecrease width={20} height={20} />}
            stylingMode="text"
            disabled={disabled}
            className="!px-2"
            onClick={handleOutdentButtonClick}
          />
        )
      },
      [handleOutdentButtonClick]
    )

    // インデントボタン
    const handleIndentButtonClick = useCallback(() => {
      const treeList = treeListRef.current?.instance
      if (!treeList) return

      const currentFocusedRowKey = treeList.option('focusedRowKey')
      const selectedNode = treeList.getNodeByKey(currentFocusedRowKey)

      // キーでノードが見つからない場合は何もしない
      if (!selectedNode || !selectedNode.parent) {
        return
      }

      const selectedNodeIndexFromParent =
        selectedNode.parent.children?.findIndex(
          (node) => node.key === selectedNode.key
        )

      // ノードが見つからない場合(-1)、またはインデックスが0か未定義（兄弟内で一番最初の場合）は何もしない
      if (
        selectedNodeIndexFromParent === undefined ||
        selectedNodeIndexFromParent === -1 ||
        selectedNodeIndexFromParent === 0
      ) {
        return
      }

      const previousNode =
        selectedNode.parent.children?.[selectedNodeIndexFromParent - 1]

      // 前のノードが見つからない場合はぶら下げ先がないので何もしない
      if (!previousNode) return

      // 移動先の階層が階層制限内かどうかをチェックする
      if (
        !isWithinTaskDepthLimit(selectedNode, previousNode, maxTasksDepthLimit)
      ) {
        toast.error(t('message.apiError.taskDepthLimitExceeded'))
        return
      }

      // 選択したノードの親taskOrderを、前のノードのtaskOrderに更新した上でstateを更新する
      const updateTaskTemplateTasks = props.taskTemplateTasks.map((task) => {
        if (task.taskOrder === selectedNode.key) {
          return {
            ...task,
            parentTaskTemplateTaskOrder:
              (previousNode.data?.taskOrder as string) ?? null,
          }
        }
        return task
      })

      // taskOrderを再採番
      const { tasks: reassignedTaskTemplateTasks, orderMap } =
        reassignTaskOrder(updateTaskTemplateTasks)

      const recalculatedTasks = recalcParentRequiredDays(
        reassignedTaskTemplateTasks
      )
      props.setTaskTemplateTasks(recalculatedTasks)

      // インデント後のフォーカス対象のkeyを再計算
      const nextFocusedKey =
        typeof currentFocusedRowKey === 'string'
          ? (orderMap.get(currentFocusedRowKey) ?? currentFocusedRowKey)
          : undefined

      if (nextFocusedKey) {
        updateFocusedRowKey(nextFocusedKey)
      }

      treeList.expandRow(orderMap.get(previousNode.key) ?? previousNode.key)
    }, [maxTasksDepthLimit, props, updateFocusedRowKey, t, toast])

    // インデントボタンのカスタムレンダー
    const renderIndentButton = useCallback(
      ({ disabled }: { disabled: boolean }) => {
        return (
          <CrewButton
            icon={<FormatIndentIncrease width={20} height={20} />}
            stylingMode="text"
            disabled={disabled}
            className="!px-2"
            onClick={handleIndentButtonClick}
          />
        )
      },
      [handleIndentButtonClick]
    )

    // タスク追加ボタン
    const handleAddTaskButtonClick = useCallback(() => {
      const focusedRowKey =
        treeListRef.current?.instance.option('focusedRowKey')
      const selectedTaskTemplateTask =
        treeListRef.current?.instance.getNodeByKey(focusedRowKey)?.data

      // 選択されたタスクがある場合は、そのタスクと同じ階層に新規タスクを作成する
      const parentOrder =
        selectedTaskTemplateTask?.parentTaskTemplateTaskOrder ?? null

      // 新しいtaskOrderを生成
      const newOrder = generateNextTaskOrder(
        parentOrder,
        props.taskTemplateTasks
      )

      const newTaskTemplateTask: TreeListTaskTemplateTask = {
        ...initTaskTemplateTaskValues,
        taskOrder: newOrder,
        parentTaskTemplateTaskOrder: parentOrder,
      }

      const mergedTaskTemplateTasks = [
        ...props.taskTemplateTasks,
        newTaskTemplateTask,
      ]

      props.setTaskTemplateTasks(
        recalcParentRequiredDays(mergedTaskTemplateTasks)
      )

      // 新しいタスクにフォーカスを設定（onFocusedRowChangedが発火して右パネルが更新される）

      treeListRef.current?.instance.option('focusedRowKey', newOrder)
    }, [initTaskTemplateTaskValues, props])

    // タスク追加ボタンのカスタムレンダー
    const renderAddTaskButton = useCallback(() => {
      return (
        <CrewButton
          icon={<EventAvailable width={20} height={20} />}
          text={t('action.addTask')}
          stylingMode="outlined"
          onClick={handleAddTaskButtonClick}
        />
      )
    }, [handleAddTaskButtonClick, t])

    // 子タスク追加ボタン
    // 子タスク追加ボタンがクリックされた時のイベントハンドラ
    const handleAddChildTaskButtonClick = useCallback(() => {
      const focusedRowKey =
        treeListRef.current?.instance.option('focusedRowKey')

      if (!focusedRowKey) return

      // 新しいtaskOrderを生成（親はfocusedRowKey）
      const newOrder = generateNextTaskOrder(
        focusedRowKey,
        props.taskTemplateTasks
      )

      const newTaskTemplateTask: TreeListTaskTemplateTask = {
        ...initTaskTemplateTaskValues,
        taskOrder: newOrder,
        parentTaskTemplateTaskOrder: focusedRowKey,
      }

      const mergedTaskTemplateTasks = [
        ...props.taskTemplateTasks,
        newTaskTemplateTask,
      ]

      props.setTaskTemplateTasks(
        recalcParentRequiredDays(mergedTaskTemplateTasks)
      )

      // 親行を展開して、新しい子タスクにフォーカスを設定（onFocusedRowChangedが発火して右パネルが更新される）
      treeListRef.current?.instance.expandRow(focusedRowKey)
      treeListRef.current?.instance.option('focusedRowKey', newOrder)
    }, [initTaskTemplateTaskValues, props])

    // 子タスク追加ボタンのカスタムレンダー
    const renderAddChildTaskButton = useCallback(() => {
      return (
        <CrewButton
          icon={<AccountTreeOutline width={20} height={20} />}
          text={t('action.addSubtask')}
          stylingMode="outlined"
          disabled={!focusedRowKey}
          onClick={handleAddChildTaskButtonClick}
        />
      )
    }, [focusedRowKey, handleAddChildTaskButtonClick, t])

    // タスク削除ボタン
    const handleDeleteButtonClick = useCallback(() => {
      const focusedRowKey =
        treeListRef.current?.instance.option('focusedRowKey')
      const selectedNode =
        treeListRef.current?.instance.getNodeByKey(focusedRowKey)
      if (!selectedNode) return
      // 子タスクがある場合は、子タスクもすべて削除されることを警告
      if (selectedNode.hasChildren) {
        setConfirmMessage(t('message.task.confirmDeleteLinkedSubtasks'))
      } else {
        setConfirmMessage(t('message.general.confirmMessage.delete'))
      }
      openConfirmDialog()
    }, [openConfirmDialog, t])

    // タスク削除ボタンのカスタムレンダー
    const renderDeleteTaskButton = useCallback(() => {
      return (
        <CrewButton
          icon={<BaselineDelete width={20} height={20} />}
          text={t('action.deleteTask')}
          stylingMode="outlined"
          disabled={!focusedRowKey}
          onClick={handleDeleteButtonClick}
        />
      )
    }, [focusedRowKey, handleDeleteButtonClick, t])

    // ---------- Toolbarのボタン系の処理ここまで

    // ---------- ドラッグアンドドロップ関連の処理

    // ドラッグ開始時にフォーカス変更イベントを無視するフラグを立てる
    const onDragStart = useCallback(() => {
      isDraggingRef.current = true
    }, [])

    // ドラッグ中のイベントハンドラ（自身または子孫へのドロップを防ぐ）
    const onDragChange = useCallback((e: any) => {
      const component = e.component
      const visibleRows = component.getVisibleRows()

      const sourceNode = component.getNodeByKey(e.itemData.taskOrder)
      const targetRow = visibleRows[e.toIndex]
      let targetNode = targetRow?.node

      if (!sourceNode || !targetNode) return

      let current = targetNode
      while (current?.data) {
        if (current.data.taskOrder === sourceNode.data.taskOrder) {
          e.cancel = true
          return
        }
        current = current.parent
      }
    }, [])

    // ドラッグ終了時（キャンセル含む）にフラグを解除
    // setTimeoutすることによって、ドラッグ終了イベントと同時に走るその他イベントを先に走らせてからフラグを解除できる
    const onDragEnd = useCallback(() => {
      setTimeout(() => {
        isDraggingRef.current = false
      }, 0)
    }, [])

    // ドラッグアンドドロップで並び替えた時のイベントハンドラ
    const onReorder = useCallback(
      (e: any) => {
        // フォーカスイベントが飛んできてもフォームを汚さないようドラッグ中はスキップ
        isDraggingRef.current = true

        const visibleRows = e.component.getVisibleRows()
        let sourceData = { ...e.itemData } as TreeListTaskTemplateTask
        const updatedTaskTemplateTasks = [...props.taskTemplateTasks]

        const sourceIndex = updatedTaskTemplateTasks.findIndex(
          (task) => task.taskOrder === sourceData.taskOrder
        )

        // 現在フォーカス中の行（ドラッグ対象とは限らない）を保持
        // A, Bの2行で、Aをフォーカス時にBをドラッグした場合など
        const currentFocusedOrder =
          treeListRef.current?.instance.option('focusedRowKey') ?? focusedRowKey

        // 現在の展開状態を保持（キーは旧taskOrder基準）
        const prevExpandedKeys =
          (treeListRef.current?.instance.option('expandedRowKeys') as
            | string[]
            | undefined) ?? []

        const expandCandidates: string[] = []

        if (e.dropInsideItem) {
          // アイテムの中にドロップされた場合（子タスクとして追加）
          // 先に階層チェックを行う
          if (maxTasksDepthLimit) {
            // ドラッグ先のノード＝親ノードになるノード
            const parentNode = visibleRows[e.toIndex]?.node
            // ドラッグ対象のノード
            const sourceNode = e.component.getNodeByKey(e.itemData.taskOrder)

            if (parentNode && sourceNode) {
              // 移動先の階層が階層制限内かどうかをチェックする
              if (
                !isWithinTaskDepthLimit(
                  sourceNode,
                  parentNode,
                  maxTasksDepthLimit
                )
              ) {
                toast.error(t('message.apiError.taskDepthLimitExceeded'))

                // ドラッグ処理完了後にフォーカスイベントを受け付ける
                // setTimeoutすることによって、onReorderと同時に走るその他イベントを先に走らせてからフラグを解除できる
                setTimeout(() => {
                  isDraggingRef.current = false
                }, 0)
                // 操作をキャンセル（早期リターン）
                return
              }
            }
          }

          const targetTaskOrder = visibleRows[e.toIndex]?.key as string

          sourceData = {
            ...sourceData,
            parentTaskTemplateTaskOrder: targetTaskOrder,
          }

          updatedTaskTemplateTasks.splice(sourceIndex, 1)
          updatedTaskTemplateTasks.splice(e.toIndex, 0, sourceData)

          // 親行を展開してぶら下げた子を表示（後で新keyに置き換える）
          expandCandidates.push(targetTaskOrder)
        } else {
          // アイテムの間にドロップされた場合（兄弟として追加）
          const toIndex = e.fromIndex > e.toIndex ? e.toIndex - 1 : e.toIndex
          const targetHasChildren =
            visibleRows[toIndex]?.node?.hasChildren ?? false
          let targetData =
            toIndex >= 0
              ? (visibleRows[toIndex]?.node?.data as TreeListTaskTemplateTask)
              : null

          if (
            targetData &&
            e.component.isRowExpanded(targetData.taskOrder) &&
            targetHasChildren
          ) {
            // ターゲットが展開されていて子を持つ場合、その子の一番上に挿入
            sourceData = {
              ...sourceData,
              parentTaskTemplateTaskOrder: targetData.taskOrder,
            }
            targetData = null
          } else {
            // それ以外の場合、ターゲットと同じ親を持つ兄弟として挿入
            const parentTaskOrder = targetData
              ? targetData.parentTaskTemplateTaskOrder
              : null
            if (sourceData.parentTaskTemplateTaskOrder !== parentTaskOrder) {
              sourceData = {
                ...sourceData,
                parentTaskTemplateTaskOrder: parentTaskOrder,
              }
            }
          }

          updatedTaskTemplateTasks.splice(sourceIndex, 1)
          const targetIndex = targetData
            ? updatedTaskTemplateTasks.findIndex(
                (task) => task.taskOrder === targetData?.taskOrder
              ) + 1
            : 0
          updatedTaskTemplateTasks.splice(targetIndex, 0, sourceData)
        }

        // taskOrderを再採番（ドラッグアンドドロップ時は配列順序を維持）
        // この時点では、まだ配列の順番を変更しているだけなので、taskOrderを再採番し、整合を取る必要がある
        const { tasks: reassignedTasks, orderMap } = reassignTaskOrder(
          updatedTaskTemplateTasks,
          true
        )

        // かつ、親の所要日数を再計算してstateを更新
        props.setTaskTemplateTasks(recalcParentRequiredDays(reassignedTasks))

        // 展開状態を新しいtaskOrderに追従させる
        const mappedExpanded = prevExpandedKeys.map(
          (key) => orderMap.get(key) || key
        )
        const mappedCandidates = expandCandidates.map(
          (key) => orderMap.get(key) || key
        )
        const nextExpandedKeys = _.uniq([
          ...mappedExpanded,
          ...mappedCandidates,
        ])
        if (treeListRef.current) {
          treeListRef.current.instance.option(
            'expandedRowKeys',
            nextExpandedKeys
          )
        }

        // フォーカス行（ドラッグ対象ではなく元のフォーカス）を新しいtaskOrderに追従させる
        if (currentFocusedOrder && treeListRef.current) {
          const newFocusedOrder = orderMap.get(currentFocusedOrder)
          if (newFocusedOrder) {
            updateFocusedRowKey(newFocusedOrder)
          }
        }

        // ドラッグ処理完了後にフォーカスイベントを受け付ける
        // setTimeoutすることによって、onReorderと同時に走るその他イベントを先に走らせてからフラグを解除できる
        setTimeout(() => {
          isDraggingRef.current = false
        }, 0)
      },
      [focusedRowKey, maxTasksDepthLimit, props, updateFocusedRowKey, t, toast]
    )

    // ---------- ドラッグアンドドロップ関連の処理ここまで

    // ハンドラ

    // 担当者に自分を設定するリンク
    const handleAssignTextLinkClick = useCallback(() => {
      if (loggedInUser) {
        setValue('assignToUserId', loggedInUser.id)
      }
    }, [loggedInUser, setValue])

    // 削除確認ダイアログ OKボタン
    const handleDeletePermitButtonClick = useCallback(() => {
      const focusedRowKey =
        treeListRef.current?.instance.option('focusedRowKey')
      if (!focusedRowKey) {
        closeConfirmDialog()
        return
      }

      // 削除対象のtaskOrderを収集（自分自身と子孫すべて）
      const deleteTaskTemplateTaskOrders: string[] = [focusedRowKey]

      // 子孫のtaskOrderを収集
      const collectDescendantTaskOrders = (parentOrder: string) => {
        const children = props.taskTemplateTasks.filter(
          (task) => task.parentTaskTemplateTaskOrder === parentOrder
        )
        children.forEach((child) => {
          deleteTaskTemplateTaskOrders.push(child.taskOrder)
          collectDescendantTaskOrders(child.taskOrder)
        })
      }

      collectDescendantTaskOrders(focusedRowKey)

      // 重複を除去
      const uniqueDeleteTaskTemplateTaskOrders = _.uniq(
        deleteTaskTemplateTaskOrders
      )

      // 削除対象以外のタスクで新しい配列を作成
      const remainingTaskTemplateTasks = props.taskTemplateTasks.filter(
        (task) => !uniqueDeleteTaskTemplateTaskOrders.includes(task.taskOrder)
      )

      // taskOrderを再採番
      const { tasks: reassignedTasks } = reassignTaskOrder(
        remainingTaskTemplateTasks
      )
      props.setTaskTemplateTasks(recalcParentRequiredDays(reassignedTasks))

      // フォーカスをクリア
      updateFocusedRowKey(undefined)

      // フォームを初期化
      reset(initTaskTemplateTaskValues)

      closeConfirmDialog()
    }, [
      closeConfirmDialog,
      initTaskTemplateTaskValues,
      props,
      reset,
      updateFocusedRowKey,
    ])

    // フォーカス行が変更された時のイベントハンドラ
    const handleFocusedRowChanged = useCallback(
      (event: FocusedRowChangedEvent) => {
        // ドラッグ中はフォーム値の保存をスキップする
        if (isDraggingRef.current) {
          return
        }

        // 現在フォームに入力されている値で、focusedRowKeyが設定されている場合は、変更を保存する
        if (focusedRowKey) {
          let hasChanged = false
          const mergedTasks = props.taskTemplateTasks.map((task) => {
            if (task.taskOrder === focusedRowKey) {
              // リスト側の表示にはrefが必要なので、dataSourceから取得する
              const newSubject = getValues('subject')
              const newTaskKindId = getValues('taskKindId')
              const newTaskKind = newTaskKindId
                ? taskKindDataSource
                    .items()
                    .find((taskKind) => taskKind.id === newTaskKindId) || null
                : null
              const newTaskPriority = getValues('taskPriority')
              const newAssignToUserId = getValues('assignToUserId')
              const newAssignToUser = newAssignToUserId
                ? memberDataSource
                    .items()
                    .find((member) => member.id === newAssignToUserId) || null
                : null
              const newRequiredDays = getValues('requiredDays')
              const newDescription = getValues('description') ?? ''

              hasChanged =
                task.subject !== newSubject ||
                task.taskKindId !== newTaskKindId ||
                (task.taskKind?.id ?? null) !== (newTaskKind?.id ?? null) ||
                task.taskPriority !== newTaskPriority ||
                (task.assignToUserId ?? null) !== (newAssignToUserId ?? null) ||
                (task.assignToUser?.id ?? null) !==
                  (newAssignToUser?.id ?? null) ||
                (task.requiredDays ?? null) !== (newRequiredDays ?? null) ||
                (task.description ?? '') !== newDescription

              if (!hasChanged) {
                return task
              }

              return {
                ...task,
                subject: newSubject,

                taskKindId: newTaskKindId,
                taskKind: newTaskKind,

                taskPriority: newTaskPriority,

                assignToUserId: newAssignToUserId,
                assignToUser: newAssignToUser,

                requiredDays: newRequiredDays,
                description: newDescription,
              }
            }
            return task
          })

          // 変更があった場合のみstateを更新
          // state更新による無駄な再レンダリングを防ぐため
          if (hasChanged) {
            props.setTaskTemplateTasks(recalcParentRequiredDays(mergedTasks))
          }
        }

        // 新しい行のデータを取得
        const newRowData = event.row?.data

        if (!newRowData) {
          return
        }

        // フォーカス行キーを設定
        // この処理はtreeList側のフォーカス更新によって発火されているので、treeList側を改めて更新する必要はない。
        // そのためupdateFocusedRowKeyではなくsetFocusedRowKeyを直接呼び出す。
        setFocusedRowKey(newRowData.taskOrder)

        // 選択した行の情報を、右側のフォームにセットする
        reset({
          subject: newRowData.subject,
          taskKindId: newRowData.taskKindId,
          taskPriority: newRowData.taskPriority,
          assignToUserId: newRowData.assignToUserId ?? null,
          requiredDays: newRowData.requiredDays ?? null,
          description: newRowData.description ?? '', // nullの場合は空文字に変換
        })
      },
      [
        focusedRowKey,
        getValues,
        memberDataSource,
        props,
        reset,
        taskKindDataSource,
      ]
    )

    // レンダー
    // 必須入力マークのレンダー
    const renderRequiredIndicator = useCallback(
      (idPrefix: string) => {
        const popoverId = `${idPrefix}-${uniqueString()}`
        const message = t('message.general.required')

        return (
          <div id={popoverId} className="flex items-center">
            <BaselineError width={20} height={20} className="crew-error-text" />
            <CrewPopover
              target={`#${popoverId}`}
              showEvent="mouseenter"
              hideEvent="mouseleave"
              position="top"
              maxWidth={300}
            >
              <span className="text-sm">{message}</span>
            </CrewPopover>
          </div>
        )
      },
      [t]
    )

    // タスク件名のセルレンダー
    const renderSubjectCell = useCallback(
      ({ value }: { value: string }) => {
        const isEmpty = !value || value.trim() === ''
        if (!isEmpty) return value

        return (
          <div className="flex items-center gap-x-2.5 w-full">
            <span className="truncate flex-1">{value}</span>
            <div className="ml-auto">
              {renderRequiredIndicator('subject-error')}
            </div>
          </div>
        )
      },
      [renderRequiredIndicator]
    )

    // タスク種別のセルレンダー
    const renderTaskKindCell = useCallback(
      ({ value }: { value: TaskKindRef | null }) => {
        if (!value) {
          return (
            <div className="flex items-center gap-x-2.5 w-full">
              <span className="flex-1" />
              <div className="ml-auto">
                {renderRequiredIndicator('taskkind-error')}
              </div>
            </div>
          )
        }

        return (
          <div className="flex items-center gap-x-2.5">
            <CrewBadge displayColor={value.displayColor}>
              {value.name}
            </CrewBadge>
          </div>
        )
      },
      [renderRequiredIndicator]
    )

    // 優先度のセルレンダー
    const renderTaskPriorityCell = useCallback(
      ({ value }: { value: number | null | undefined }) => {
        const taskPriority =
          value !== null && value !== undefined
            ? getEnumDataByValue(TaskPriorities, value)
            : null

        if (!taskPriority) {
          return (
            <div className="w-full flex items-center gap-x-2.5">
              <span className="flex-1" />
              <div className="ml-auto">
                {renderRequiredIndicator('priority-error')}
              </div>
            </div>
          )
        }

        return (
          <div className="w-full flex items-center gap-x-2.5">
            <CrewTaskPriority taskPriority={taskPriority.value} />
          </div>
        )
      },
      [renderRequiredIndicator]
    )

    // 担当者のセルレンダー
    const renderAssignToUserCell = useCallback(
      ({ value }: { value: UserRef | null }) => {
        if (!value) return ''
        return (
          <CrewUserAvatar
            userId={value.id}
            displayName={value.displayName}
            size={CrewAvatarSize.xs}
            showLabel={true}
            cacheValue={value.id + value.version}
          />
        )
      },
      []
    )

    // 所要日数のセルレンダー
    const renderRequiredDaysCell = useCallback(
      ({
        value,
        data,
      }: {
        value: number | null
        data: TreeListTaskTemplateTask
      }) => {
        if (!value) return ''
        const isParent = taskOrdersHavingChildren.includes(data.taskOrder)
        return (
          <div className="w-full flex justify-end">
            <span className={isParent ? 'crew-text-gray-5' : undefined}>
              {t('label.taskTemplate.valueOfRequiredDays', { value: value })}
            </span>
          </div>
        )
      },
      [t, taskOrdersHavingChildren]
    )

    return (
      <>
        <div className="flex flex-row gap-x-2.5 overflow-y-auto">
          {/* 左側: タスク一覧 */}
          {/** Toolbarが縮小されないため、min-wを付けている */}
          <div className="w-3/5 min-w-[620px] overflow-y-auto">
            <TreeList
              ref={treeListRef}
              dataSource={props.taskTemplateTasks}
              keyExpr="taskOrder"
              parentIdExpr="parentTaskTemplateTaskOrder"
              autoExpandAll={true}
              showBorders={true}
              showRowLines={true}
              height="600px"
              focusedRowEnabled={true}
              onFocusedRowChanged={handleFocusedRowChanged}
            >
              {/* ドラッグアンドドロップ */}
              <RowDragging
                onDragStart={onDragStart}
                onDragChange={onDragChange}
                onDragEnd={onDragEnd}
                onReorder={onReorder}
                allowDropInsideItem={true}
                allowReordering={true}
                showDragIcons={true}
              />
              {/** タスク件名 */}
              <Column
                dataField="subject"
                caption={t('label.taskSubject')}
                width="300px"
                cellRender={renderSubjectCell}
              />

              {/** 種別 */}
              <Column
                dataField="taskKind"
                caption={t('label.taskKindName')}
                cellRender={renderTaskKindCell}
              />

              {/** 優先度 */}
              <Column
                alignment="left"
                dataField="taskPriority"
                caption={t('label.taskPriority')}
                cellRender={renderTaskPriorityCell}
              />

              {/** 担当者 */}
              <Column
                dataField="assignToUser"
                caption={t('label.assignToUserName')}
                cellRender={renderAssignToUserCell}
              />

              {/** 所要日数 */}
              <Column
                alignment="left"
                dataField="requiredDays"
                caption={t('label.taskTemplate.requiredDays')}
                cellRender={renderRequiredDaysCell}
              />

              {/** ツールバー */}
              <Toolbar>
                {/* すべて閉じる */}
                <Item
                  widget="dxButton"
                  options={collapseAllButtonOptions}
                  location="before"
                />

                {/* すべて展開 */}
                <Item
                  widget="dxButton"
                  options={expandAllButtonOptions}
                  location="before"
                />

                {/* アウトデント */}
                <Item render={renderOutdentButton} location="before" />

                {/* インデント */}
                <Item render={renderIndentButton} location="before" />

                {/* タスク追加 */}
                <Item render={renderAddTaskButton} location="before" />

                {/* 子タスク追加 */}
                <Item render={renderAddChildTaskButton} location="before" />

                {/* タスク削除 */}
                <Item render={renderDeleteTaskButton} location="before" />
              </Toolbar>
            </TreeList>
          </div>

          {focusedRowKey && (
            <div className="flex flex-col gap-y-2.5 w-2/5 min-w-0 p-2.5 overflow-y-auto">
              {/** タスク名 */}
              <CrewTextBoxField
                id="subject"
                name="subject"
                control={control}
                rules={validateRules.subject}
                label={t('label.taskSubject')}
                required
                // inputにしないとフォームにフォーカスが当たったままのときにgetValuesで値を取得できない
                valueChangeEvent="input"
              />
              <div className="flex flex-row gap-x-2.5">
                {/** 種別 */}
                <div className="w-1/3">
                  <CrewBadgeSelectBoxField
                    id="taskKindId"
                    name="taskKindId"
                    control={control}
                    rules={validateRules.taskKindId}
                    dataSource={taskKindDataSource}
                    labelMode="hidden"
                    searchEnabled={false}
                    // NOTE: searchEnabled=falseだとデフォルト値の"1"が適用となりloadされないので"0"を設定
                    minSearchLength={0}
                    label={t('label.classification')}
                    required={true}
                    deferRendering={false}
                    showClearButton={false}
                    grouped={false}
                  />
                </div>
                {/** 優先度 */}
                <div className="w-1/3">
                  <CrewSelectBoxField
                    id="taskPriority"
                    name="taskPriority"
                    control={control}
                    rules={validateRules.taskPriority}
                    dataSource={taskPriorityDataSource}
                    fieldRender={CrewTaskPrioritySelectBoxFieldRender}
                    itemRender={CrewTaskPrioritySelectBoxItemRender}
                    labelMode="hidden"
                    valueExpr="id"
                    displayExpr="name"
                    searchEnabled={false}
                    minSearchLength={0}
                    label={t('label.priority')}
                    required={true}
                    showClearButton={false}
                  />
                </div>

                {/* 担当者 */}
                <div className="w-1/3">
                  <div className="flex flex-col gap-y-1">
                    <div className="flex items-center justify-between">
                      <CrewFieldLabel text={t('label.assignedUser')} />

                      {/* 私が担当 */}
                      <CrewLink
                        className="text-sm underline"
                        onClick={handleAssignTextLinkClick}
                      >
                        {t('label.assignToMe')}
                      </CrewLink>
                    </div>
                    <CrewSelectBoxField
                      id="assignToUserId"
                      name="assignToUserId"
                      control={control}
                      rules={validateRules.assignToUserId}
                      dataSource={memberDataSource}
                      labelMode="hidden"
                      valueExpr="id"
                      displayExpr="displayName"
                      searchExpr="displayName"
                      searchEnabled={true}
                      minSearchLength={0}
                    />
                  </div>
                </div>
              </div>
              {/** 所要日数 */}
              <div className="flex flex-col gap-y-1 w-1/3">
                <div className="flex flex-row items-center gap-x-1">
                  <CrewFieldLabel text={t('label.taskTemplate.requiredDays')} />
                  {focusedRowHasChildren && (
                    <div
                      id={requiredDaysPopoverId}
                      className="crew-warning-text"
                    >
                      <BaselineWarning width={16} height={16} />
                      <CrewPopover
                        target={`#${requiredDaysPopoverId}`}
                        showEvent="mouseenter"
                        hideEvent="mouseleave"
                        position="top"
                        maxWidth={320}
                      >
                        <span className="text-sm">
                          {t('label.taskTemplate.requiredDaysWarning')}
                        </span>
                      </CrewPopover>
                    </div>
                  )}
                </div>
                <CrewNumberBoxField
                  id="requiredDays"
                  name="requiredDays"
                  control={control}
                  showLabel={false}
                  disabled={focusedRowHasChildren}
                  // inputにしないとフォームにフォーカスが当たったままのときにgetValuesで値を取得できない
                  valueChangeEvent="input"
                />
              </div>
              {/** 説明 */}

              <CrewHtmlEditorField
                id="description"
                name="description"
                control={control}
                label={t('label.description')}
                // inputにしないとフォームにフォーカスが当たったままのときにgetValuesで値を取得できない
                valueChangeEvent="input"
              />
            </div>
          )}
        </div>
        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={confirmMessage}
          onPermitButtonClick={handleDeletePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
        />
      </>
    )
  })
)
